import React from 'react';
import { Video, Clock, Laptop, CheckCircle, ArrowRight, ShieldCheck, HeartPulse, MessageSquare } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { clinicInfo } from '../data/mockData';

const OnlinePhysiotherapyPage = () => {
  const handleWhatsApp = () => {
    window.open(`https://wa.me/${clinicInfo.whatsapp.replace(/[^0-9]/g, '')}?text=Hi, I would like to book an online physiotherapy consultation`, '_blank');
  };

  const benefits = [
    {
      title: "Expert Care Anywhere",
      description: "Consult with our specialist physiotherapists from the comfort of your home or office.",
      icon: Laptop
    },
    {
      title: "Flexible Scheduling",
      description: "Book appointments that fit your busy lifestyle, including early morning and late evening slots.",
      icon: Clock
    },
    {
      title: "Evidence-Based Guidance",
      description: "Same high-quality clinical protocols and personalized exercise programs as in-person visits.",
      icon: ShieldCheck
    },
    {
      title: "Cost & Time Effective",
      description: "Save on travel time and costs while receiving expert attention and monitoring.",
      icon: HeartPulse
    }
  ];

  const steps = [
    {
      id: "01",
      title: "Book Your Slot",
      description: "Schedule your consultation via WhatsApp or our booking link."
    },
    {
      id: "02",
      title: "Initial Assessment",
      description: "A 45-minute video call to discuss your history, symptoms, and functional goals."
    },
    {
      id: "03",
      title: "Personalized Plan",
      description: "Receive a tailored exercise program with video demonstrations and guidance."
    },
    {
      id: "04",
      title: "Regular Monitoring",
      description: "Follow-up sessions to track progress and adjust your treatment for optimal results."
    }
  ];

  const faqs = [
    {
      question: "Is online physiotherapy as effective as in-person?",
      answer: "Research shows that for many musculoskeletal conditions, tele-physiotherapy is just as effective as face-to-face treatment, especially when focused on exercise-based rehabilitation and education."
    },
    {
      question: "What equipment do I need?",
      answer: "Most assessments can be done with items you already have at home. We'll guide you on how to use everyday objects for your exercises if specialized equipment isn't available."
    },
    {
      question: "What platform do you use for video calls?",
      answer: "We typically use WhatsApp Video, Zoom, or Google Meet, depending on your preference and convenience."
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative py-20 md:py-32 bg-primary text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-white via-transparent to-transparent" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block px-4 py-1.5 bg-secondary text-primary font-bold text-sm rounded-full mb-6">
                Now Available Globally
              </span>
              <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
                Online Physiotherapy & Tele-Rehab
              </h1>
              <p className="text-xl text-white/90 mb-10 leading-relaxed">
                Expert clinical assessment and evidence-led recovery programs, delivered directly to your screen. No travel, no waiting—just recovery.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  onClick={handleWhatsApp}
                  size="lg" 
                  className="bg-white text-primary hover:bg-gray-100 text-lg px-8 py-6 h-auto"
                >
                  Book Your Online Session
                </Button>
                <Button 
                  variant="outline" 
                  size="lg" 
                  className="border-2 border-white text-white hover:bg-white hover:text-primary text-lg px-8 py-6 h-auto"
                  onClick={() => document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' })}
                >
                  How It Works
                </Button>
              </div>
            </div>
            <div className="hidden md:block">
              <div className="relative">
                <div className="absolute -inset-4 bg-secondary/20 rounded-2xl blur-2xl" />
                <img 
                  src="https://images.unsplash.com/photo-1584432810601-6c7f27d2362b?w=800&q=80" 
                  alt="Tele-physiotherapy Session" 
                  className="rounded-2xl shadow-2xl relative z-10 w-full aspect-[4/3] object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Grid */}
      <section className="py-20 bg-muted">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4">Why Choose Online Consult?</h2>
            <p className="text-lg text-muted-foreground">Premium care tailored to your lifestyle</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <Card key={index} className="border-none shadow-lg hover:shadow-xl transition-shadow duration-300">
                <CardContent className="p-8">
                  <div className="w-14 h-14 bg-secondary/50 rounded-xl flex items-center justify-center mb-6">
                    <benefit.icon className="w-7 h-7 text-primary" />
                  </div>
                  <h3 className="text-xl font-bold text-primary mb-3">{benefit.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">{benefit.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How it Works Section */}
      <section id="how-it-works" className="py-20 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-primary mb-8">Four Steps to Recovery</h2>
              <div className="space-y-10">
                {steps.map((step) => (
                  <div key={step.id} className="flex gap-6">
                    <span className="text-4xl font-bold text-secondary flex-shrink-0">{step.id}</span>
                    <div>
                      <h3 className="text-xl font-bold text-primary mb-2">{step.title}</h3>
                      <p className="text-muted-foreground leading-relaxed">{step.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-secondary/10 p-8 md:p-12 rounded-3xl border border-secondary/20">
              <h3 className="text-2xl font-bold text-primary mb-6 flex items-center gap-3">
                <Video className="w-6 h-6" />
                What's included?
              </h3>
              <ul className="space-y-4">
                {[
                  "Complete clinical screening",
                  "Movement and posture assessment",
                  "Personalized PDF exercise guide",
                  "Video demonstration of exercises",
                  "Direct WhatsApp access for queries",
                  "Weekly progress review"
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-lg text-gray-700">
                    <CheckCircle className="w-6 h-6 text-primary flex-shrink-0 mt-0.5" />
                    {item}
                  </li>
                ))}
              </ul>
              <Button 
                onClick={handleWhatsApp}
                className="w-full mt-10 bg-primary hover:bg-primary/90 text-white h-14 text-lg"
              >
                Get Started Today
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-muted">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-primary text-center mb-12">Frequently Asked Questions</h2>
          <div className="space-y-6">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                <h4 className="text-lg font-bold text-primary mb-2">{faq.question}</h4>
                <p className="text-muted-foreground leading-relaxed">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-primary text-white text-center">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Ready to start your recovery?</h2>
          <p className="text-xl text-white/90 mb-10">
            Join hundreds of patients who have successfully recovered through our online programs.
          </p>
          <Button 
            onClick={handleWhatsApp}
            size="lg" 
            className="bg-secondary text-primary hover:bg-secondary/90 text-lg px-12 py-7 h-auto font-bold"
          >
            Book Your Consult via WhatsApp
          </Button>
        </div>
      </section>
    </div>
  );
};

export default OnlinePhysiotherapyPage;
